/**
 * Custom summary — browser-direct call to Gemini with the user's (or shared) API
 * key, answering the rep's per-user questions from the meeting transcript only.
 *
 * Runs live during the meeting (see useLiveCustomSummary) and again, finalized, on
 * the summary page. Transcript-only: no web-search grounding, so every answer is
 * either stated in the call ("meeting") or "not-found". The key resolves via
 * effectiveGeminiKey() — the user's own key, or the shared key for VIP users.
 */

import type { CustomAnswer, TranscriptLine } from "@scoach/types";

import { effectiveGeminiKey } from "../../../../lib/geminiKey.ts";
import { callGemini, textOf } from "./quietAsk.ts";

const SYSTEM = `You are Sally, a sales-meeting assistant. You are given a meeting transcript and a list of QUESTIONS the rep wants answered. Answer EVERY question using ONLY what is in the transcript.

Rules:
- Use ONLY information actually present in the transcript. Never invent, guess, or use outside knowledge.
- If a question is answered (even partially) in the transcript, answer it concisely (1-3 sentences; a short comma-separated list is fine for list answers) and set "source" to "meeting".
- If the transcript does NOT contain the answer, set "answer" to "Not discussed in the meeting" and "source" to "not-found".
- Answer in the SAME language as the question.
- Preserve the questions exactly as given, in the same order.

Return STRICT JSON only (no markdown fences):
{
  "answers": [
    { "question": "<the question, verbatim>", "answer": "<concise answer or 'Not discussed in the meeting'>", "source": "meeting"|"not-found" }
  ]
}`;

function transcriptToText(transcript: TranscriptLine[]): string {
  return transcript.map((l) => `[${l.t}] ${l.name} (${l.lang}): ${l.text}`).join("\n");
}

function extractJson(raw: string): string {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenced?.[1]) return fenced[1].trim();
  const start = raw.indexOf("{");
  if (start === -1) return raw.trim();
  let depth = 0;
  for (let i = start; i < raw.length; i++) {
    if (raw[i] === "{") depth++;
    else if (raw[i] === "}" && --depth === 0) return raw.slice(start, i + 1);
  }
  return raw.slice(start);
}

/**
 * Answer the rep's custom questions from the transcript on the user's Gemini key.
 * Returns answers re-keyed to the exact question list (same order), so a dropped or
 * reordered model answer never shifts the rep's list. Returns [] when there's no
 * key, no questions, or no transcript.
 */
export async function generateCustomSummary(
  transcript: TranscriptLine[],
  questions: string[],
): Promise<CustomAnswer[]> {
  const key = effectiveGeminiKey();
  const qs = (questions ?? []).map((q) => q.trim()).filter(Boolean);
  if (!key || qs.length === 0 || transcript.length === 0) return [];

  const userPrompt = `Questions to answer:
${qs.map((q, i) => `${i + 1}. ${q}`).join("\n")}

Transcript:
${transcriptToText(transcript)}`;

  const body = {
    systemInstruction: { role: "system", parts: [{ text: SYSTEM }] },
    contents: [{ role: "user", parts: [{ text: userPrompt }] }],
    generationConfig: {
      temperature: 0.2,
      maxOutputTokens: 4096,
      responseMimeType: "application/json",
    },
  };

  const json = await callGemini(key, body);
  const raw = textOf(json).trim();
  if (!raw) return [];

  let parsed: { answers?: Array<{ question?: string; answer?: string; source?: string }> } = {};
  try {
    parsed = JSON.parse(extractJson(raw) || "{}");
  } catch {
    return [];
  }
  const answers = Array.isArray(parsed.answers) ? parsed.answers : [];
  const byQuestion = new Map<string, (typeof answers)[number]>();
  for (const a of answers) {
    if (a.question) byQuestion.set(a.question.trim().toLowerCase(), a);
  }

  return qs.map((q): CustomAnswer => {
    const a = byQuestion.get(q.toLowerCase());
    const source: CustomAnswer["source"] = a?.source === "meeting" ? "meeting" : "not-found";
    return {
      question: q,
      answer: (a?.answer ?? "").trim() || "Not discussed in the meeting",
      source,
    };
  });
}
